(ns leg.scroll
  "Where a page was left, so it can be opened there again.

  Somebody reading a long article on a phone puts it down, comes back, and
  finds the first line again - which is three thumb-swipes of looking for
  the paragraph they were in. The same thing happens to a list: forty
  articles in, into one of them, out again, and the list is back at the
  top. So every box that scrolls is asked to remember, and the place is
  kept where it outlives the window: `localStorage`.

  A place is one string, and it is what the box is showing rather than the
  box itself: an article's id, or a list's address (`list-place`). The box
  is never the key, because there is one box per pane and every article is
  drawn into the same one.

  Two hundred of them are kept, most recently left first, and the rest fall
  off the end. A reader who has opened ten thousand articles should not be
  carrying ten thousand numbers around, and the one they want back is
  always one of the last few.

  Writing is put off a quarter of a second (`write-after`): a scroll fires
  sixty events a second and `setItem` is synchronous, so writing on every
  one of them would be a reader's own finger stuttering the page. What is
  in memory is the truth in the meantime, which is what the restore reads
  anyway.

  What is written down is not the same thing for both. An article is a
  pixel: the text in it does not move. A list is a *row* - which row was
  under the top edge of the box and how far it had been scrolled past -
  because the list does move. Reading an article marks it read, the next
  fetch of an `Unread` list leaves it out, and every row below it comes up
  by the height of the one that went. A pixel written down against a list
  is a row only until that happens; the id of the row is still the row
  afterwards.

  Reading back is two different questions, and that is why there are two
  functions. An article is put where it was left every time one is opened,
  because the box it is drawn in is the same box the last one was in and
  what is in it has just been replaced. A list is put there only while
  nobody has touched it: it is drawn before it is filled in and it grows
  while it is read, and a box that jumped to a remembered position every
  time the list changed underneath it would take the page away from the
  finger holding it."
  (:require [kc.core :as kc]))

(def storage-key "leser.scroll.v1")

(def keep-places
  "How many places are remembered at once."
  200)

(def ^:private write-after
  "Milliseconds between a scroll and the write it causes."
  250)

;; Every place, most recently left first, as `[place top]`. Nil until
;; something has asked for one: the first read is a trip to localStorage,
;; and a window where nothing is ever scrolled never takes it.
(defonce ^:private !marks (atom nil))

(defonce ^:private !timer (atom nil))

(defn- storage [] (when (exists? js/localStorage) js/localStorage))

(defn- stored []
  (or (when-let [s (storage)]
        (try (let [v (kc/read-edn (.getItem s storage-key))]
               (when (vector? v) v))
             (catch :default _ nil)))
      []))

(defn- marks [] (or @!marks (reset! !marks (stored))))

(defn with-mark
  "`marks` with `place` at the front at `top`, and nothing of it anywhere
  else. Pure, and the whole of the bookkeeping: the order *is* which ones
  are worth keeping, so this is the one part of this file with a test."
  [marks place top]
  (into [[place top]]
        (comp (remove #(= place (first %)))
              (take (dec keep-places)))
        marks))

(defn list-place
  "The place a list is: its address, filter and all.

  The rows in it are deliberately *not* part of this any more. They were,
  for one release, and the argument was that a pixel means a row and only
  while nothing above it has moved - so a list whose rows had changed was a
  list nobody had been. It is a sound argument about pixels and it threw
  the position away constantly, because the commonest change to a list is
  the one the reader just made: the article they opened is read now, the
  next fetch of an `Unread` list does not carry it, and the list they come
  back to is never the list they left.

  So the position is kept against the address and what is written down
  under it is a row (`row-at-top`), which survives the list changing
  underneath. The filter stays in the address, as it always was: `Unread`
  and `All` are two lists of different lengths and a row in one is not a
  row in the other."
  [href]
  (str href))

(defn row-at-top
  "Which row the top edge of `box` is in, and how far it has been scrolled
  past it - or nil when there is no row there.

  One hit test and not a walk over the rows: this runs on every frame of a
  scroll, and a list is as long as the reader's week. The point is a few
  pixels in from the left edge so a scrollbar is not what answers."
  [^js box]
  (let [r   (.getBoundingClientRect box)
        el  (.elementFromPoint js/document (+ (.-left r) 8) (+ (.-top r) 1))
        row (some-> el (.closest "[data-row]"))]
    (when row
      {:row    (.getAttribute row "data-row")
       ;; How much of the row is above the edge: nought when it starts
       ;; exactly there, and never negative, because the row the edge is in
       ;; is the row it is in.
       :offset (js/Math.round (- (.-top r) (.. row getBoundingClientRect -top)))})))

(defn mark
  "Where `place` was left, or nil - which is the first line."
  [place]
  (some (fn [[p top]] (when (= p place) top)) (marks)))

(defn- write! []
  (when-let [s (storage)]
    (try (.setItem s storage-key (kc/->edn @!marks))
         ;; A full disk, a private window, a quota - none of them are worth
         ;; a message to somebody who was only scrolling.
         (catch :default _ nil))))

(defn remember!
  "`place` is at `where` - a number of pixels for an article, or a row and
  an offset for a list. Called for every frame of a scroll."
  [place where]
  (when (and place where)
    (let [where (if (number? where) (max 0 (js/Math.round where)) where)]
      (swap! !marks #(with-mark (or % (stored)) place where)))
    (when-not @!timer
      (reset! !timer (js/setTimeout #(do (reset! !timer nil) (write!)) write-after)))))

(defn on-scroll
  "A scroll handler for the box showing `place`. Cheap enough to make on
  every render: everything that is held onto between events is up there in
  an atom."
  [place]
  (fn [e] (remember! place (.. e -currentTarget -scrollTop))))

(defn on-list-scroll
  "The same, for a list: what goes down is the row under the top edge."
  [place]
  (fn [^js e]
    (let [box (.-currentTarget e)]
      (when-let [at (row-at-top box)]
        (remember! place at)))))

(defn to-mark!
  "`box` to where `place` was left, or to its first line if it was never
  left anywhere. No animation: this is not a move somebody made, it is the
  position the page is opened at."
  [box place]
  (when box
    (.scrollTo box #js {:top (or (mark place) 0) :behavior "auto"})))

(defn to-row-untouched!
  "`box` back to the row it was left on, for a box that is filled in after
  it is drawn - and therefore only while it is still at its first line.

  That one test is the whole of the second half of it, and it answers two
  questions at once. A box nobody has scrolled is a box that has just
  appeared: freshly drawn, or empty a moment ago and now holding a list,
  and either way the place it is showing is the place to go to. A box that
  is anywhere else has somebody reading it, and a list that grew by a
  refresh under their thumb is left exactly where they had it.

  The first half is the row. It is looked for in this box and nowhere else,
  and if it is not here there is nothing to do: a list that no longer holds
  the row somebody was on is a list they have not been down, and it opens
  at the top like any other first visit. `scrollBy` and not `scrollTo`,
  because what is known is where the row is *now* and how far above the
  edge it belongs.

  A number is what an older window wrote down here, and it is still a
  position: it is used as one."
  [^js box place]
  (when (and box place (< (.-scrollTop box) 1))
    (let [m (mark place)]
      (cond
        (map? m)
        (let [{:keys [row offset]} m]
          (when-let [el (and row (.querySelector box (str "[data-row=\"" row "\"]")))]
            (let [d (- (.. el getBoundingClientRect -top)
                       (.. box getBoundingClientRect -top))
                  by (+ d (or offset 0))]
              (when-not (zero? by)
                (.scrollBy box #js {:top by :behavior "auto"})))))

        (and (number? m) (pos? m))
        (.scrollTo box #js {:top m :behavior "auto"})))))
